import { EMAIL_RULES_SYSTEM_ENUMS, SystemEvent } from '@erp/shared-types';
import type { EventTriggerConfig } from '@erp/workflows';
import { useMemo, useState } from 'react';
import useCoreEnums from 'src/api/core/core-enums/useCoreEnums';
import { Autocomplete } from 'src/components/common/inputs/autocomplete';
import { Label } from 'src/components/ui/label';

type EmailRuleEventSubConfigConfig = Partial<EventTriggerConfig> &
  Record<string, unknown>;

export function EmailRuleEventSubConfig({
  config,
  onConfigChange,
}: {
  config: EmailRuleEventSubConfigConfig;
  onConfigChange: (newConfig: EmailRuleEventSubConfigConfig) => void;
}) {
  const rawEmailRuleOptionId = config.emailRuleOptionId;
  const emailRuleOptionId =
    typeof rawEmailRuleOptionId === 'string' ? rawEmailRuleOptionId : '';

  const [searchValue, setSearchValue] = useState('');

  const {
    data: coreEnumsData,
    isLoading,
    error,
  } = useCoreEnums({
    name: EMAIL_RULES_SYSTEM_ENUMS.EMAIL_RULE_EVENTS.name,
    includeOptions: true,
    first: 1,
  });

  const emailRuleEnum = coreEnumsData?.edges?.[0];

  const options = useMemo(() => {
    const raw = emailRuleEnum?.options ?? [];
    return raw
      .toSorted((a, b) => {
        const sort = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        if (sort !== 0) return sort;
        return (a.name ?? '').localeCompare(b.name ?? '');
      })
      .filter((opt) => Boolean(opt.id));
  }, [emailRuleEnum?.options]);

  return (
    <div className="space-y-2">
      <Label htmlFor="emailRuleOptionId" className="text-sm font-medium">
        Email Rule
      </Label>

      <Autocomplete
        options={options}
        getValue={(option) => option.id}
        getLabel={(option) => option.name ?? option.id}
        searchValue={searchValue}
        onSearchValueChange={setSearchValue}
        value={emailRuleOptionId}
        onValueChange={(newValue) => {
          onConfigChange({
            ...config,
            eventType: SystemEvent.EMAIL_ROUTED_EMAIL_RULE,
            emailRuleOptionId: newValue || undefined,
          });
        }}
        placeholder={isLoading ? 'Loading rules…' : 'Select an email rule…'}
        searchPlaceholder="Search rules…"
        emptyMessage={
          error ? 'Failed to load email rules.' : 'No email rule events found.'
        }
        multiSelect={false}
        disabled={isLoading || Boolean(error) || options.length === 0}
      />

      {error && (
        <p className="text-xs text-destructive">
          Could not load email rule events from API.
        </p>
      )}

      {!error && !isLoading && !emailRuleEnum && (
        <p className="text-xs text-muted-foreground">
          Core enum "Email Rule Events" was not found.
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        The workflow will start when an email matches this rule.
      </p>
    </div>
  );
}
